import React, { useState, useEffect, useLayoutEffect } from 'react';
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './screens/Login';
import PlansPage from './screens/Plans/index';
import ExperimentPage from './screens/Experiment/index';
import PromoCodesPage from './screens/PromoCodes/index';
import SubscriptionsPage from './screens/Subscriptions/index';
import VideoLessonsPage from './screens/VideoLessons/index';
import CashflowPage from './screens/Cashflow/index';
import ExpensesPage from './screens/Expenses/index';
import FoundersPage from './screens/Founders/index';
import NotificationsPage from './screens/Notifications/index';
import CreateNotificationPage from './screens/CreateNotification/index';
import NotificationAnalyticsPage from './screens/NotificationAnalytics/index';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import { AlertsProvider } from './contexts/AlertsContext';
import DashboardPage from './screens/Dashboard/index';
import OrdersPaymentsPage from './screens/OrdersPayments/index';
import { cn } from './lib/utils';
import { Toaster } from './components/ui/sonner';
import { ConfirmProvider } from './components/shared/ConfirmDialog';
import UsersPage from './screens/Users/index';
import SettingsPage from './screens/Settings/index';
import SecurityPage from './screens/Security/index';
import GamesPage from './screens/Games/index';
import ExamAttemptsPage from './screens/ExamAttempts/index';
import WrongAnswersPage from './screens/WrongAnswers/index';
import AuditLogPage from './screens/AuditLog/index';
import AppConfigPage from './screens/AppConfig/index';
import BannersPage from './screens/Banners/index';
import DuplicatesPage from './screens/Duplicates/index';

interface MainLayoutProps {
  children: React.ReactNode;
  isDark: boolean;
  setIsDark: (value: boolean) => void;
}

// Main Layout: сайдбар 256px слева (на узком экране — выезжающее меню), справа шапка + контент.
const MainLayout: React.FC<MainLayoutProps> = ({ children, isDark, setIsDark }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Мобильное меню закрывается по Esc.
  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileMenuOpen]);

  return (
    <AlertsProvider>
      <div className="min-h-screen bg-bg text-text">
        {/* Sidebar (desktop) */}
        <div className="fixed inset-y-0 left-0 z-40 hidden w-64 lg:block">
          <Sidebar />
        </div>

        {/* Mobile Sidebar Overlay */}
        {mobileMenuOpen && (
          <div
            className="fixed inset-0 z-40 bg-hero/55 lg:hidden"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile Sidebar */}
        <div
          className={cn(
            // Ширина задана явно: без неё -translate-x-full сдвигал обёртку нулевой ширины на 0
            // и меню оставалось видимым поверх страницы на узких экранах.
            'fixed left-0 top-0 z-50 h-screen w-64 transition-transform duration-300 lg:hidden',
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          )}
          // Закрытое меню недоступно ни мыши, ни Tab.
          inert={!mobileMenuOpen}
        >
          <Sidebar
            // На узком экране после перехода меню закрывается, иначе оно перекрывает страницу.
            onNavigate={() => setMobileMenuOpen(false)}
          />
        </div>

        <div className="flex min-h-screen min-w-0 flex-col lg:pl-64">
          <Header
            isDark={isDark}
            setIsDark={setIsDark}
            mobileMenuOpen={mobileMenuOpen}
            onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
          />
          <main className="w-full min-w-0 px-4 py-5 lg:px-7 lg:py-7">
            {children}
          </main>
        </div>
      </div>
    </AlertsProvider>
  );
};

function App() {
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('adminDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Класс .dark на <html> и запоминание выбора. useLayoutEffect — чтобы класс стоял
  // до useEffect детей (useTokenColors читает CSS-переменные уже новой темы).
  useLayoutEffect(() => {
    localStorage.setItem('adminDarkMode', JSON.stringify(isDark));
    const root = document.documentElement;
    if (root.classList.contains('dark') === isDark) return;
    // На время смены темы гасим transition-ы, иначе кнопки «доезжают» до новых цветов.
    root.classList.add('theme-switching');
    root.classList.toggle('dark', isDark);
    const frame = requestAnimationFrame(() =>
      requestAnimationFrame(() => root.classList.remove('theme-switching')),
    );
    return () => {
      cancelAnimationFrame(frame);
      root.classList.remove('theme-switching');
    };
  }, [isDark]);

  return (
    <div className="App">
      <ConfirmProvider isDark={isDark}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login isDark={isDark} setIsDark={setIsDark} />} />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainLayout isDark={isDark} setIsDark={setIsDark}>
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/users" element={<UsersPage />} />
                      <Route path="/duplicates" element={<DuplicatesPage />} />
                      <Route path="/security" element={<SecurityPage />} />
                      <Route path="/plans" element={<PlansPage />} />
                      <Route path="/experiment" element={<ExperimentPage />} />
                      <Route path="/video-lessons" element={<VideoLessonsPage />} />
                      <Route path="/promo-codes" element={<PromoCodesPage />} />
                      {/* Один раздел «Заказы и платежи»: вкладка определяется адресом. */}
                      <Route path="/orders" element={<OrdersPaymentsPage />} />
                      <Route path="/payments" element={<OrdersPaymentsPage />} />
                      <Route path="/subscriptions" element={<SubscriptionsPage />} />
                      <Route path="/notifications" element={<NotificationsPage />} />
                      <Route path="/notifications/create" element={<CreateNotificationPage />} />
                      <Route path="/notifications/:id/analytics" element={<NotificationAnalyticsPage />} />
                      <Route path="/games" element={<GamesPage />} />
                      <Route path="/exam-attempts" element={<ExamAttemptsPage />} />
                      <Route path="/wrong-answers" element={<WrongAnswersPage />} />
                      <Route path="/audit-log" element={<AuditLogPage />} />
                      <Route path="/cashflow" element={<CashflowPage />} />
                      <Route path="/expenses" element={<ExpensesPage />} />
                      <Route path="/founders" element={<FoundersPage />} />
                      <Route path="/banners" element={<BannersPage />} />
                      <Route path="/app-config" element={<AppConfigPage />} />
                      <Route path="/settings" element={<SettingsPage />} />
                      <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                  </MainLayout>
                </ProtectedRoute>
              }
            />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster theme={isDark ? 'dark' : 'light'} />
      </ConfirmProvider>
    </div>
  );
}

export default App;

